import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Failures,
  KeyValue,
  Metadata,
  Query,
  SimpleTableRow,
  SortDirection,
  TableHeader,
} from "../../interfaces";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { AppBody } from "../AppBody";
import LinkComponent from "../LinkComponent";
import {
  ByStateFilterOptions,
  ByYearFilterOptions,
  DetailsFilterOptions,
} from "../HistoricalBankData/FieldSelectionOptions";
import { buildQueryUrl, fetchData } from "../../common/services/ApiClient";
import {
  appHeaderTitle,
  appTitles,
  fileNameFormat,
  getExpandState,
} from "../../common/services/Utilities";
import SimpleTableBody from "../MultiHeaderTable/SimpleTableBody";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import ApiDataDownload from "../ApiDataDownload";
import ResultPagination from "../../containers/ResultPagination";
import BankFailuresSearchForm from "../../containers/BankFailuresSearchForm";
import { selectedStatesAlpha } from "../../refdata/FilterOptions";
import Breadcrumbs from "../../containers/Breadcrumbs";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";

const PREFIX = "BankFailures";

const classes = {
  tableStyles: `${PREFIX}-tableStyles`,
  buttonStyle: `${PREFIX}-buttonStyle`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.tableStyles}`]: {
    "& #multi-header-table": {
      "marginTop": 0,
      "& #multi-header-table-head": {
        backgroundColor: "#003256",
      },
    },
    "& #tableWrapper": {
      marginTop: 8,
    },
    "& th": {
      zIndex: 5,
      position: "sticky",
      top: 0,
      height: "100%",
      minHeight: 55,
      maxHeight: 55,
      width: 40,
    },
    "& tr:nth-child(odd) > td:nth-child(1)": {
      position: "sticky",
      left: 0,
      zIndex: 10,
      backgroundColor: "#D2E1EE",
    },
    "& tr:nth-child(even) > td:nth-child(1)": {
      position: "sticky",
      left: 0,
      zIndex: 10,
      backgroundColor: "#D9E8F6",
    },
    "&#details_table": {
      "& tr > th#institution-name-header-0": {
        "position": "sticky",
        "left": 0,
        "zIndex": 20,
        "backgroundColor": "#003256",
        "& span": {
          width: 120,
        },
      },
      "& tr > th#cert-header-1": {
        position: "sticky",
        left: 136,
        zIndex: 20,
        backgroundColor: "#003256",
      },
      "& tr:nth-child(odd) > td:nth-child(2)": {
        position: "sticky",
        left: 136,
        zIndex: 10,
        backgroundColor: "#D2E1EE",
      },
      "& tr:nth-child(even) > td:nth-child(2)": {
        position: "sticky",
        left: 136,
        zIndex: 10,
        backgroundColor: "#D9E8F6",
      },
    },
    "&#by_state_table": {
      "& tr > th#state-header-0": {
        position: "sticky",
        left: 0,
        zIndex: 20,
        backgroundColor: "#003256",
      },
      "& tr > th#total-count-header-1": {
        position: "sticky",
        left: 49,
        zIndex: 20,
        backgroundColor: "#003256",
      },
      "& tr:nth-child(odd) > td:nth-child(2)": {
        position: "sticky",
        left: 49,
        zIndex: 10,
        backgroundColor: "#D2E1EE",
      },
      "& tr:nth-child(even) > td:nth-child(2)": {
        position: "sticky",
        left: 49,
        zIndex: 10,
        backgroundColor: "#D9E8F6",
      },
    },
    "&#by_year_table": {
      "& tr > th#year-header-0": {
        position: "sticky",
        left: 0,
        zIndex: 20,
        backgroundColor: "#003256",
      },
      "& tr > th#total-count-header-1": {
        position: "sticky",
        left: 61,
        zIndex: 20,
        backgroundColor: "#003256",
      },
      "& tr:nth-child(odd) > td:nth-child(2)": {
        position: "sticky",
        left: 61,
        zIndex: 10,
        backgroundColor: "#D2E1EE",
      },
      "& tr:nth-child(even) > td:nth-child(2)": {
        position: "sticky",
        left: 61,
        zIndex: 10,
        backgroundColor: "#D9E8F6",
      },
    },
  },

  [`& .${classes.buttonStyle}`]: {
    "color": theme.palette.primary.main,
    "border": "0px solid",
    "borderRadius": "3px",
    "padding": 0,
    "height": "auto",
    "textDecoration": "underline",
    "&:focus, &:hover": {
      color: "#BB3300",
      backgroundColor: "#E6F3FF",
      textDecoration: "underline",
    },
    "&:active": {
      color: "#fff",
      backgroundColor: theme.palette.primary.main,
      textDecoration: "none",
    },
  },
}));

type FailuresApiField = keyof Failures;

interface Props {
  appState: Query;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
  updateFilter: (filter: string, value: string) => void;
  updateAggValues: (agg: any) => void;
}

interface FailuresHeaders {
  title: string;
  apiField: FailuresApiField;
}

const detailsHeaders = [].concat.apply(
  [],

  DetailsFilterOptions.map((report: KeyValue) => {
    return {
      title: `${report.label}${report.thousandsUS ? "*" : ""}`,
      apiField: report.value,
      thousandsUS: report.thousandsUS,
    };
  }) as never[],
);

const byStateHeaders = [].concat.apply(
  [],
  ByStateFilterOptions.map((report: KeyValue) => {
    return {
      title: `${report.label}${report.thousandsUS ? "*" : ""}`,
      apiField: report.value,
      thousandsUS: report.thousandsUS,
    };
  }) as never[],
);

const byYearHeaders = [].concat.apply(
  [],
  ByYearFilterOptions.map((report: KeyValue) => {
    return {
      title: `${report.label}${report.thousandsUS ? "*" : ""}`,
      apiField: report.value,
      thousandsUS: report.thousandsUS,
    };
  }) as never[],
);

export function failuresHeaderLookup(report: string) {
  const headers: KeyValue = {
    details: detailsHeaders,
    by_state: byStateHeaders,
    by_year: byYearHeaders,
  };
  return headers[report];
}

function formatData(data: KeyValue, apiField: string) {
  const thousandsUS = [
    "COST",
    "QBFASSET",
    "sum_QBFASSET",
    "sum_QBFDEP",
    "sum_COST",
    "QBFDEP",
  ];
  if (apiField === "PSTALP") {
    return selectedStatesAlpha.map((state) =>
      state.value === data[apiField] ? state.label : "",
    );
  } else if (apiField === "COST" && data[apiField] === null) {
    return "N/A";
  } else if (data[apiField] && thousandsUS.includes(apiField)) {
    return Math.round(data[apiField]).toLocaleString();
  } else if (data[apiField] && apiField !== "CERT") {
    return data[apiField].toLocaleString();
  }
  return data[apiField];
}

export function transformFailuresData(
  failuresData: KeyValue[],
  headerData: FailuresHeaders[],
) {
  return failuresData.map((data: KeyValue) => {
    const tableCellData: SimpleTableRow = {};
    headerData
      .map((header: FailuresHeaders) => header.apiField)
      .forEach((apiField: string) => {
        return (tableCellData[apiField] = {
          value: formatData(data, apiField),
        });
      });

    return data.title
      ? { ...tableCellData, NAME: { value: data.title } }
      : tableCellData;
  });
}

export default function BankFailures({
  appState,
  updateSort,
  updateAggValues,
  updateFilter,
}: Props) {
  const [tableNote, setTableNote] = React.useState("");
  const [failuresData, setFailuresData] = React.useState<Failures[]>([]);
  const [metaData, setMetaData] = React.useState<Metadata>({
    count: 0,
    totals: [] as KeyValue[],
  });

  React.useEffect(() => {
    if (!appState.filters.endDate) {
      const currentYear = new Date().getFullYear().toString();
      updateFilter("endDate", currentYear);
    }
  }, [appState.filters.endDate, updateFilter]);

  const staticHeaders: FailuresHeaders[] =
    appState.ui.aggReport === "details"
      ? [
          { title: "Institution Name", apiField: "NAME" },
          { title: "Cert", apiField: "CERT" },
        ]
      : appState.ui.aggReport === "by_state"
        ? [
            { title: "State", apiField: "PSTALP" },
            { title: "Total Count", apiField: "count" },
          ]
        : [
            { title: "Year", apiField: "FAILYR" },
            { title: "Total Count", apiField: "count" },
          ];
  const headerValues = appState.ui.aggReport
    ? [...staticHeaders, ...failuresHeaderLookup(appState.ui.aggReport)].filter(
        (value, index, array) =>
          array.findIndex(
            (arrayVal) => arrayVal.apiField === value.apiField,
          ) === index,
      )
    : staticHeaders;

  React.useEffect(() => {
    setTableNote("");
    return headerValues.forEach((header) => {
      if (header.thousandsUS) {
        return setTableNote("* Numbers represent thousands of US Dollars");
      }
    });
  }, [headerValues]);

  const failuresHeaders: TableHeader[][] = [
    headerValues.map((header: FailuresHeaders) => ({
      title: header.title,
      styles: {},
      colSpan: 1,
      apiField: header.apiField,
    })),
  ];
  const introParagraph = (
    <>
      <Grid container spacing={2}>
        <Grid size={12}>{appHeaderTitle("failures")}</Grid>
      </Grid>
      <Typography variant="body2" id="failures-description">
        BankFind Suite's Failures and Assistance provides a complete look at
        bank failures and assistance transactions of FDIC-insured institutions
        from 1934 to the present. The data is updated quarterly.
      </Typography>
      <LinkComponent appName={"Failures"} />
    </>
  );

  function getAggregateStatus(report: string) {
    const reportValues: KeyValue = {
      by_state: {
        agg_by: "PSTALP",
        agg_term_fields: ["RESTYPE"],
        agg_sum_fields: ["QBFASSET", "QBFDEP", "COST"],
        subtotal_by: null,
        total_fields: null,
      },
      by_year: {
        agg_by: "FAILYR",
        agg_term_fields: ["RESTYPE", "SAVR", "CHCLASS1", "RESTYPE1"],
        agg_sum_fields: null,
        subtotal_by: null,
        total_fields: null,
      },
      details: {
        agg_by: null,
        agg_term_fields: null,
        agg_sum_fields: null,
        subtotal_by: "RESTYPE",
        total_fields: ["QBFDEP", "QBFASSET", "COST"],
      },
    };
    return reportValues[report];
  }
  const includePagination = (children: React.ReactNode) =>
    appState.ui.aggReport === "details" ? (
      <ResultPagination count={metaData.count}>
        <>{children}</>
      </ResultPagination>
    ) : (
      <>{children}</>
    );

  const [previousReport, setPreviousReport] = React.useState(
    appState.ui.aggReport,
  );

  React.useEffect(() => {
    if (!appState.agg.agg_by && !appState.agg.agg_term_fields) {
      updateAggValues(getAggregateStatus(appState.ui.aggReport));
    }
  }, [
    appState.agg.agg_by,
    appState.agg.agg_term_fields,
    appState.ui.aggReport,
    updateAggValues,
  ]);
  React.useEffect(() => {
    if (previousReport !== appState.ui.aggReport) {
      updateSort(
        appState.ui.aggReport === "by_state" ? "PSTALP" : "FAILYR",
        "ASC",
      );
      setPreviousReport(appState.ui.aggReport);
      updateAggValues(getAggregateStatus(appState.ui.aggReport));
    }
  }, [previousReport, appState.ui.aggReport, updateSort, updateAggValues]);

  const updatedAppState = React.useMemo(() => {
    return {
      ...appState,
      filters: {
        ...appState.filters,
        selectedStatesAlpha:
          appState.filters.selectedStatesAlpha.length > 0
            ? appState.filters.selectedStatesAlpha
            : [undefined],
        selectedInsuranceFunds:
          appState.filters.selectedInsuranceFunds.length > 0
            ? appState.filters.selectedInsuranceFunds
            : [undefined],
        selectedCharterTypes:
          appState.filters.selectedCharterTypes.length > 0
            ? appState.filters.selectedCharterTypes
            : [undefined],
        selectedTransactionTypes:
          appState.filters.selectedTransactionTypes.length > 0
            ? appState.filters.selectedTransactionTypes
            : [undefined],
      },
    };
  }, [appState]);

  const [prevAppState, setPrevAppState] = React.useState({});

  const [makeApiCall, setMakeApiCall] = React.useState(false);
  React.useEffect(() => {
    let isMounted = true;
    if (
      JSON.stringify(updatedAppState) !== JSON.stringify(prevAppState) &&
      isMounted
    ) {
      setPrevAppState(updatedAppState);
      setMakeApiCall(true);
    }
    return () => {
      isMounted = false;
    };
  }, [updatedAppState, prevAppState]);

  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (makeApiCall) {
      let isMounted = true;
      setMakeApiCall(false);
      setLoading(true);
      (async () => {
        await fetchData(buildQueryUrl("/failures", updatedAppState))
          .then((response) => {
            if (isMounted) {
              const totalsResponse = [
                { ...response.totals, RESTYPE: response.totals.count },
              ];
              const subValues: KeyValue[] = [];
              if (response.totals.subtotal_by_RESTYPE) {
                response.totals.subtotal_by_RESTYPE.map((values: any) => {
                  return subValues.push({
                    ...values,
                    RESTYPE: values.count,
                    title: `TOTAL ${values.RESTYPE}`,
                  });
                });
              }
              // Temporary solution to add states & years with no values into the results table. Will be removed once api is updated to include data with no values
              const statesWithData = response.data.map(
                (item: Failures) => item.PSTALP,
              );
              const selectedStates = appState.filters.selectedStatesAlpha;
              const statesWithNoData = selectedStates.filter(
                (i: string) => !statesWithData.includes(i),
              );
              const statesWithNoDataObj = statesWithNoData.map((e: string) => {
                return {
                  PSTALP: e,
                  count: "0",
                  count_RESTYPE_FAILURE: "0",
                  sum_COST: "0",
                  sum_QBFASSET: "0",
                  sum_QBFDEP: "0",
                };
              });

              if (appState.ui.aggReport === "by_state") {
                Array.prototype.push.apply(response.data, statesWithNoDataObj);
              }

              if (appState.sort.sortField === "PSTALP") {
                response.data.sort((a: any, b: any) =>
                  appState.sort.sortOrder === "ASC"
                    ? a.PSTALP > b.PSTALP
                      ? 1
                      : -1
                    : a.PSTALP < b.PSTALP
                      ? 1
                      : -1,
                );
              }
              const rangeOfYears = (start: any, end: any) =>
                Array(end - start + 1)
                  .fill(start)
                  .map((year, index) => year + index);

              const selectedYears = rangeOfYears(
                Number(appState.filters.startDate),
                Number(appState.filters.endDate || new Date().getFullYear()),
              );
              const yearsWithData = response.data.map((item: Failures) =>
                Number(item.FAILYR),
              );
              const yearsWithNoData = selectedYears.filter(
                (i) => !yearsWithData.includes(i),
              );
              const yearsWithNoDataObj = yearsWithNoData.map((year: number) => {
                return {
                  FAILYR: year.toString().replace(",", ""),
                  count: "0",
                  count_CHCLASS1_N: "0",
                  count_CHCLASS1_NM: "0",
                  count_CHCLASS1_SA: "0",
                  count_CHCLASS1_SB: "0",
                  count_CHCLASS1_SI: "0",
                  count_CHCLASS1_SM: "0",
                  count_RESTYPE1_PA: "0",
                  count_RESTYPE1_PO: "0",
                  count_RESTYPE_FAILURE: "0",
                  count_SAVR_DIF: "0",
                };
              });
              if (appState.ui.aggReport === "by_year") {
                Array.prototype.push.apply(response.data, yearsWithNoDataObj);
              }
              if (appState.sort.sortField === "FAILYR") {
                response.data.sort((a: any, b: any) =>
                  appState.sort.sortOrder === "ASC"
                    ? a.FAILYR > b.FAILYR
                      ? 1
                      : -1
                    : a.FAILYR < b.FAILYR
                      ? 1
                      : -1,
                );
              }
              setFailuresData(response.data);

              setMetaData({
                count: response.totals.count,
                totals: [...subValues, ...totalsResponse],
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            setLoading(false);
            isMounted = false;
          });
      })();
    }
  }, [appState, makeApiCall, updatedAppState]);

  function sortHandler(tableHeader: TableHeader) {
    if (tableHeader.apiField && appState.sort) {
      const sortDirection =
        appState.sort.sortField === tableHeader.apiField &&
        appState.sort.sortOrder === "ASC"
          ? "DESC"
          : "ASC";
      updateSort(tableHeader.apiField, sortDirection);
    }
  }
  const sorting = {
    sortHandler,
    sortField: appState.sort ? appState.sort.sortField : "",
    sortDirection: appState.sort ? appState.sort.sortOrder : "ASC",
  };
  useInitialPageLoad("failures");

  return (
    <Root>
      <Breadcrumbs currentPageBreadCrumbTitle={appTitles("failures")} />
      <CloudFoundryBanner />
      <AppBody
        introText={introParagraph}
        searchForm={<BankFailuresSearchForm appState={appState} />}
        expand={getExpandState(appState.ui.searchPanelExpand, true)}
      >
        <>
          <Typography
            component="div"
            id="multi-header-table-title"
            marginTop={-1}
          >
            <Box
              id="resultSummary"
              sx={{
                display: "flex",
                alignItems: "end",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h2">
                {metaData.count.toLocaleString()}{" "}
                {metaData.count === 1 ? "Matching Result" : "Matching Results"}
              </Typography>

              <ApiDataDownload
                app="failures"
                numberOfResults={metaData.count}
                apiData={[
                  {
                    appState: appState,
                    fileName: `${fileNameFormat("Summary of Results")}.csv`,
                    apiPath: "failures",
                  },
                ]}
              />
            </Box>
            <Divider style={{ marginBottom: "1rem" }} />
          </Typography>

          {includePagination(
            <div
              className={classes.tableStyles}
              id={`${appState.ui.aggReport}_table`}
            >
              {tableNote !== "" ? (
                <Typography
                  variant="h4"
                  id="tableNote"
                  style={{ paddingBottom: 0 }}
                >
                  {tableNote}
                </Typography>
              ) : (
                ""
              )}

              <MultiHeaderTable
                sortProperties={sorting}
                tableHeaders={failuresHeaders}
                tableBody={
                  loading ? (
                    <></>
                  ) : (
                    <SimpleTableBody
                      totalsRow={transformFailuresData(
                        metaData.totals as KeyValue[],
                        headerValues,
                      )}
                      tableData={transformFailuresData(
                        failuresData,
                        headerValues,
                      )}
                      app="failures"
                      nonInteractive
                    />
                  )
                }
              />
            </div>,
          )}
        </>
      </AppBody>
    </Root>
  );
}
